import { Action, PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from './store';
import { useParams } from 'react-router-dom';
import { useAppSelector } from './hooks';
import { DEFAULT_LANG } from '../defaults';
import { getCloudflareValues } from './api-cloudfare';
import { ConfiguratorPage } from '../navigation';
import { AllowedCurrency } from './types/api-types';
import { setActiveCurrency } from './api-client';

export type AppContext = 'app' | 'stapp';

export type AfterLoginAction = {
    type: 'loadCart3D',
    cartId: number
}

export type SalesIBMiInfo = {
    customer?: string
    pricelist?: string
    pricelistcurrency?: string
    jobnumber?: string
}

export type LoginStatus = 'initial' |
                          'not-logged' |
                          'retrieving-prev'|
                          'logging-in' |
                          'logged' |
                          'failed'| 
                          'error';

export interface MainState {
    // Country - this is obtained from Cloudfare, filled when the call succeeds
    country: string
    // In which context the application is running: nothing is the "open" website
    // app - the mobile app, stapp - standalone app
    appContext?: AppContext
    // Selected language - we are not paying attention to the user language (yet?)
    lang: string

    // login status
    loginStatus: LoginStatus
    // User id in the contexts that need an username
    userId?: string
    // Session ID
    sessionId?: string
    // Indicates is the user has been identified or not
    // (maybe it can be implicit in userId === undefined?)
    isAnonymous?: boolean
    // Page. This for the moment is the way to know where it is and
    // the action that sets it serves to init and clean-up some state.
    page?: ConfiguratorPage
    // Actions to execute after sessionId and language are set
    // This is done because page loaders are invoked in parallel,
    // not after login
    afterLoginActions: AfterLoginAction[]

    // current currency. All logged users have one
    currentCurrency?: string
    // list of allowed currencies. Only some users have this option
    allowedCurrencies?: AllowedCurrency[]

    // Tax inclusion/exclusion indicator ('e' = excluded, etc.)
    taxesin_excluded?: string

    // Info to send back verbatim to the sales green screen
    salesInfo?: SalesIBMiInfo

}

export type LoginPayload = {
    sessionId: string, 
    userId: string, 
    lang: string, 
    isAnonymous: boolean
    currency: string
    allowedCurrencies?: AllowedCurrency[]
    taxesin_excluded?: string
}

const initialState: MainState = {
    country: '*LOADING',
    lang: 'en',
    loginStatus: 'initial',
    afterLoginActions: []
}

export const mainSlice = createSlice({
    name: 'main',
    initialState,
    reducers: {
        // the override parameter is because there's an asynchronous call when cloudfare
        // tell us the country, but if we had it at some login, it must be overriden
        // but we don't know what's first.
        // The call from the answer from cloudfare won't override.
        setCountry: (state, action: PayloadAction<{ country: string, override: boolean }>) => {
            const { country, override } = action.payload;
            if(override) {
                state.country = country;
            } else if(state.country !== undefined) {
                state.country = country;
            }
            const urlCountry = getCountryFromURL();
            if (urlCountry) {
                state.country = urlCountry;
            }


        },
        setAppContext: (state, action: PayloadAction<AppContext|undefined>) => {
            state.appContext = action.payload;
        },
        setLang: (state, action: PayloadAction<string>) => {
            state.lang = action.payload;
        },
        setLoginStatus: (state, action: PayloadAction<LoginStatus>) => {
            state.loginStatus = action.payload;
        },
        enterPage: (state, action: PayloadAction<ConfiguratorPage>) => {
            state.page = action.payload;
        },
        logIn: (state, action: PayloadAction<LoginPayload>) => {
            state.sessionId = action.payload.sessionId;
            state.lang = action.payload.lang;
            state.isAnonymous = action.payload.isAnonymous;
            state.currentCurrency = action.payload.currency;
            state.allowedCurrencies = action.payload.allowedCurrencies;
            state.taxesin_excluded = action.payload.taxesin_excluded;
        },
        logout: (state) => {
            state.userId = undefined;
            state.currentCurrency = undefined;
            state.isAnonymous = undefined;
            state.sessionId = undefined;
            state.taxesin_excluded = undefined;
            state.salesInfo = undefined;
            state.loginStatus = 'not-logged';
        },
        addAfterLoginAction: (state, action: PayloadAction<AfterLoginAction>) => {
            state.afterLoginActions.push(action.payload);
        },
        clearAfterLoginActions: (state) => {
            state.afterLoginActions = [];
        },
        setSalesInfo: (state, action: PayloadAction<SalesIBMiInfo>) => {
            state.salesInfo = action.payload;                       
        }
    },
    extraReducers: (builder) => {
        builder.addCase(setCurrentCurrencyAsync.fulfilled, (state, action) => {
            state.currentCurrency = action.payload;
        })
    }
});

export const setCurrentCurrencyAsync = createAsyncThunk<string,string,{state:RootState}>(
    'main/setCurrentCurrency',
    async (currency, thunkAPI) => {
        const state = thunkAPI.getState().main;
        const sessionId = state.sessionId;
        const previousCurrency = state.currentCurrency;
        if(sessionId) {
            await setActiveCurrency(currency, sessionId);
            return currency;
        } else {
            return previousCurrency!;
        }
    }
)

export const mainActions = {
    ...mainSlice.actions, 
    setCurrentCurrency: setCurrentCurrencyAsync
};

export default mainSlice.reducer;


// ==== Selectors ====

export const appContextSelector = (state: RootState) => state.main.appContext;
export const langSelector = (state: RootState) => state.main.lang;
export const sessionIdSelector = (state: RootState) => state.main.sessionId;
export const countrySelector = (state: RootState) => state.main.country;
export const isAnonymousSelector = (state: RootState) => state.main.isAnonymous;
export const loginStatusSelector = (state: RootState) => state.main.loginStatus;
export const afterLoginActionsSelector = (state: RootState) => state.main.afterLoginActions;

export const currentCurrencySelector = (state: RootState) => state.main.currentCurrency;
export const allowedCurrenciesSelector = (state: RootState) => state.main.allowedCurrencies;
export const taxesinExcludedSelector = (state: RootState) => state.main.taxesin_excluded;

export const isRedirectedFromSalesSelector = (state: RootState) => {
    const salesInfo = state.main.salesInfo;
    if (!salesInfo) {
        return false;
    }
    // Only return true if jobnumber is present and has a non-empty string value
    const jobnumberValid = salesInfo.jobnumber && typeof salesInfo.jobnumber === 'string' && salesInfo.jobnumber.trim() !== '';
    
    return jobnumberValid;
};
export const salesInfoSelector = (state:RootState) => state.main.salesInfo;


// ==== Custom hooks ====
export function getCountryFromURL(): string | undefined {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('country')?.toUpperCase() || undefined;
}


export function useURLLang() {
    const context = useAppSelector(appContextSelector);
    const dataRequestLang = useDataRequestLang();
    if(context) {
        return context;
    } else {
        return dataRequestLang;
    }
}

export function useDataRequestLang() {
    const { langId } = useParams();
    const stateLang = useAppSelector(langSelector);
    const lang = langId ?? stateLang ?? DEFAULT_LANG;
    return lang;
}

export function useCountry(): string {
    return useAppSelector(countrySelector);
}


// ==== Thunks ====

export async function mainInit(dispatch: (a:Action) => void) {
    const cfValues = await getCloudflareValues();
    dispatch(mainSlice.actions.setCountry({ country: cfValues.loc, override: false }));
}